import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import axiosInstance from '../AxiosInstance';

const CREATE_NEW_VALUE = 'create-new';

function getCustomerLabel(customer) {
    if (!customer) {
        return '';
    }

    if (customer.externalCustomerId == null) {
        return customer.name;
    }

    return customer.integrationProviderName
        ? `${customer.name} (${customer.integrationProviderName})`
        : `${customer.name} (linked)`;
}

export default function CustomerIntegrationResolutionPage() {
    const [loading, setLoading] = useState(true);
    const [isApplying, setIsApplying] = useState(false);
    const [externalCustomers, setExternalCustomers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selections, setSelections] = useState({});
    const [status, setStatus] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);

        try {
            const [externalResponse, customersResponse] = await Promise.all([
                axiosInstance.get('/customers/externalunlinked'),
                axiosInstance.get('/customers')
            ]);

            const nextExternalCustomers = Array.isArray(externalResponse.data) ? externalResponse.data : [];
            const nextCustomers = Array.isArray(customersResponse.data) ? customersResponse.data : [];

            setExternalCustomers(nextExternalCustomers);
            setCustomers(nextCustomers);
            setSelections((currentSelections) => {
                const nextSelections = {};

                nextExternalCustomers.forEach((externalCustomer) => {
                    const externalCustomerId = externalCustomer.externalCustomerId;
                    nextSelections[externalCustomerId] =
                        currentSelections[externalCustomerId] ?? CREATE_NEW_VALUE;
                });

                return nextSelections;
            });
        } catch (error) {
            console.error('Error loading incoming customers:', error);
            setStatus({
                severity: 'error',
                message: 'Unable to load incoming customers.'
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const availableCustomers = useMemo(() => {
        return customers
            .filter((customer) => !customer.integrationProviderName)
            .sort((left, right) =>
                (left.name || '').localeCompare(right.name || '', undefined, { sensitivity: 'base' })
            );
    }, [customers]);

    const handleSelectionChange = (externalCustomerId, value) => {
        setSelections((current) => ({
            ...current,
            [externalCustomerId]: value
        }));
    };

    const saveIncomingCustomer = async (externalCustomer) => {
        const externalCustomerId = externalCustomer.externalCustomerId;
        const incomingCustomerName = externalCustomer.name || `Incoming customer ${externalCustomerId}`;
        const selectedValue = selections[externalCustomerId] ?? CREATE_NEW_VALUE;

        if (selectedValue === CREATE_NEW_VALUE) {
            await axiosInstance.post('/customers', {
                name: incomingCustomerName,
                channelId: null,
                externalCustomerId,
                isIntegrationIgnored: false
            });
            return `Created ${incomingCustomerName}.`;
        }

        const selectedCustomerId = Number(selectedValue);
        const selectedCustomer = customers.find((customer) => customer.customerId === selectedCustomerId);

        if (!selectedCustomer) {
            throw new Error('Selected customer could not be found.');
        }

        await axiosInstance.put(`/customers/${selectedCustomer.customerId}`, {
            name: selectedCustomer.name,
            channelId: selectedCustomer.channelId,
            externalCustomerId,
            isIntegrationIgnored: false
        });
        return `Linked ${incomingCustomerName} to ${selectedCustomer.name}.`;
    };

    const handleApplyAll = async () => {
        if (externalCustomers.length === 0) {
            return;
        }

        setIsApplying(true);
        setStatus(null);

        try {
            const messages = [];

            for (const externalCustomer of externalCustomers) {
                // Resolve each incoming customer using the current selection.
                messages.push(await saveIncomingCustomer(externalCustomer));
            }

            setStatus({
                severity: 'success',
                message: messages.length === 1
                    ? messages[0]
                    : `Applied ${messages.length} incoming customers.`
            });

            await fetchData();
            if (window.refreshNotifications) {
                window.refreshNotifications();
            }
        } catch (error) {
            console.error('Failed to resolve incoming customers:', error);
            setStatus({
                severity: 'error',
                message: error.response?.data || 'Failed to resolve incoming customers.'
            });
        } finally {
            setIsApplying(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                        <Typography variant="h5" component="h1">
                            Incoming customers
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            These customers have been imported from your integration and need to be resolved.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Choose whether each one should be created as a new customer or linked to an existing one.
                        </Typography>
                    </Box>
                </Box>

                {status ? (
                    <Alert severity={status.severity}>
                        {status.message}
                    </Alert>
                ) : null}

                {externalCustomers.length === 0 ? (
                    <Alert severity="info">
                        There are no incoming customers waiting for action.
                    </Alert>
                ) : (
                    <Stack spacing={2}>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Incoming customer</TableCell>
                                        <TableCell width="45%">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {externalCustomers.map((externalCustomer) => {
                                        const externalCustomerId = externalCustomer.externalCustomerId;
                                        const selectedValue = selections[externalCustomerId] ?? CREATE_NEW_VALUE;

                                        return (
                                            <TableRow key={externalCustomerId} hover>
                                                <TableCell>
                                                    <Typography variant="subtitle2">
                                                        {externalCustomer.name || `Incoming customer ${externalCustomerId}`}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {externalCustomer.provider}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel id={`incoming-customer-action-label-${externalCustomerId}`}>
                                                            Action
                                                        </InputLabel>
                                                        <Select
                                                            native
                                                            labelId={`incoming-customer-action-label-${externalCustomerId}`}
                                                            label="Action"
                                                            value={selectedValue}
                                                            onChange={(event) =>
                                                                handleSelectionChange(externalCustomerId, event.target.value)
                                                            }
                                                            inputProps={{
                                                                'data-testid': `incoming-customer-action-${externalCustomerId}`
                                                            }}
                                                        >
                                                            <option value={CREATE_NEW_VALUE}>Create new...</option>
                                                            {availableCustomers.map((customer) => (
                                                                <option key={customer.customerId} value={customer.customerId}>
                                                                    {getCustomerLabel(customer)}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleApplyAll}
                                disabled={isApplying || externalCustomers.length === 0}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {isApplying ? 'Applying...' : 'Finish import'}
                            </Button>
                        </Box>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}
